import { atom } from "jotai";
import { componentEditConfigAtom } from "./state/ComponentAtoms";
import assert from "assert";
import { filterDict } from "datasheets/model/utils/dictionaryUtils";

export const componentConnectionsAtom = (componentId: string) => {
  const configAtom = componentEditConfigAtom(componentId);
  return atom(
    (get) => {
      const parent = get(configAtom);
      if (!parent.connections) {
        console.log("No connections in ", parent);
      }
      // Handle legacy components
      return parent.connections ?? { inputs: [], outputs: [] };
    },
    (get, set, arg) => {
      const parent = get(configAtom);
      const prevConfig = parent.connections;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...parent, ...{ connections: newConfig } });
    }
  );
};

export const componentInputEndpointsAtom = (componentId: string) => {
  const configAtom = componentConnectionsAtom(componentId);
  return atom(
    (get) => {
      const parent = get(configAtom);
      return parent.inputs ?? [];
    },
    (get, set, arg) => {
      const parent = get(configAtom);
      const prevConfig = parent.inputs;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...parent, ...{ inputs: newConfig } });
    }
  );
};
export const componentInputConnectorsAtom = (componentId: string) => {
  const configAtom = componentConnectionsAtom(componentId);
  return atom(
    (get) => {
      const parent = get(configAtom);
      return parent.inputConnectors ?? [];
    },
    (get, set, arg) => {
      const parent = get(configAtom);
      const prevConfig = parent.inputConnectors;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...parent, ...{ inputConnectors: newConfig } });
    }
  );
};

export const componentOutputEndpointsAtom = (componentId: string) => {
  const configAtom = componentConnectionsAtom(componentId);
  return atom(
    (get) => {
      const parent = get(configAtom);
      if (!parent.outputs) {
        console.log("No outputs in ", parent);
      }
      return parent.outputs ?? [];
    },
    (get, set, arg) => {
      const parent = get(configAtom);
      const prevConfig = parent.outputs;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...parent, ...{ outputs: newConfig } });
    }
  );
};
export const componentOutputConnectorsAtom = (componentId: string) => {
  const configAtom = componentConnectionsAtom(componentId);
  return atom(
    (get) => {
      const parent = get(configAtom);
      return parent.outputConnectors ?? [];
    },
    (get, set, arg) => {
      const parent = get(configAtom);
      const prevConfig = parent.outputConnectors;
      const newConfig = typeof arg === "function" ? arg(prevConfig) : arg;
      set(configAtom, { ...parent, ...{ outputConnectors: newConfig } });
    }
  );
};
type EndpointConfig = { componentId: string; name: string };
export type WireConfig = {
  wireId: string;
  src: EndpointConfig;
  dest: EndpointConfig;
};
export type WireConfigMap = { [wireId: string]: WireConfig };
const wireConfigAtom = atom({} as WireConfigMap);
export const createWireAtom = atom(null, (get, set, arg) => {
  const config = arg as WireConfig;
  assert(!!config);
  const wireConfig: WireConfigMap = get(wireConfigAtom);
  const newWireConfig: WireConfigMap = {
    ...wireConfig,
    ...{ [config.wireId]: config },
  };
  set(wireConfigAtom, newWireConfig);
});
const removeInputEndpointConnectorAtom = atom(null, (get, set, arg) => {
  const endpointConfig = arg as EndpointConfig;
  assert(!!endpointConfig);
  const connectorsAtom = componentInputConnectorsAtom(
    endpointConfig.componentId
  );
  const connectors = get(connectorsAtom);
  set(
    connectorsAtom,
    filterDict(connectors, (key) => key !== endpointConfig.name)
  );
});
const removeOutputEndpointConnectorAtom = atom(null, (get, set, arg) => {
  const endpointConfig = arg as EndpointConfig;
  assert(!!endpointConfig);
  const connectorsAtom = componentOutputConnectorsAtom(
    endpointConfig.componentId
  );
  const connectors = get(connectorsAtom);
  set(
    connectorsAtom,
    filterDict(connectors, (key) => key !== endpointConfig.name)
  );
});
export const deleteWireAtom = atom(null, (get, set, arg) => {
  const wireId = arg as string;
  assert(!!wireId);
  // Remove endpoint connectors
  const wireConfig = get(wireConfigAtom);
  const config = wireConfig[wireId];
  if (!!config) {
    set(removeOutputEndpointConnectorAtom, config.src);
    set(removeInputEndpointConnectorAtom, config.dest);
    set(
      wireConfigAtom,
      filterDict(wireConfig, (key: string) => key !== wireId)
    );
  }
});
